import type { ProgressController } from './ProgressController';
import { phaseRewardRange } from '../game/gacha/gachaRules';
import type { ClearReceipt, PhaseRewardLedger } from '../game/storage/progressStore';
import type { SessionEvent, SessionState } from '../game/simulation/types';

type RewardRecord = {
  runId: string; targetPhase: number; ledger: PhaseRewardLedger | null; clearAmount: number;
  saving: boolean; error: string; clearSaving: boolean; clearError: boolean; loading: boolean; loadError: string;
};
export type RewardAward = Readonly<{ amount: number; minimum: number; phase: number }>;
export type RunRewardState = Readonly<RewardRecord & { total: number }>;
type Listener = (runId: string, award?: RewardAward) => void;

/** Owns unsaved phase entitlements across restarts, independently of game/modal state. */
export class RunRewardController {
  private readonly records = new Map<string, RewardRecord>();
  private readonly listeners = new Set<Listener>();
  constructor(private readonly getProgress: () => Promise<ProgressController>) {}

  onChange(listener: Listener): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  get(runId: string): RunRewardState {
    const record = this.record(runId);
    return { ...record, total: (record.ledger?.amount ?? 0) + record.clearAmount };
  }
  get hasErrors(): boolean {
    return [...this.records.values()].some(r => Boolean(r.error || r.loadError || r.clearError));
  }
  observe(state: SessionState, events: readonly SessionEvent[]): void {
    for (const event of events) if (event.type === 'phase-changed') {
      const record = this.record(state.runId);
      record.targetPhase = Math.max(record.targetPhase, event.phase);
      void this.save(record);
    }
  }
  clearPending(id: string): void {
    const record = this.record(id);
    record.clearSaving = true; record.clearError = false;
    this.changed(id);
  }
  clearFailed(id: string): void {
    const record = this.record(id);
    record.clearSaving = false; record.clearError = true;
    this.changed(id);
  }
  clearSaved(receipt: ClearReceipt): void {
    const record = this.record(receipt.id);
    record.clearAmount = receipt.amount; record.clearSaving = false; record.clearError = false;
    this.changed(receipt.id);
  }
  async load(id: string): Promise<void> {
    const record = this.record(id);
    if (record.loading) return;
    record.loading = true; record.loadError = ''; this.changed(id);
    try {
      const progress = await this.getProgress();
      const saved = await progress.store.loadRunRewards(id);
      // A read started before an award may finish after it; confirmed rewards only grow.
      if (saved.phase && saved.phase.highestPhase > (record.ledger?.highestPhase ?? 1)) record.ledger = saved.phase;
      record.clearAmount = Math.max(record.clearAmount, saved.clearAmount);
    } catch (error) { record.loadError = message(error); }
    finally { record.loading = false; this.changed(id); }
  }
  async retry(): Promise<void> {
    await Promise.all([...this.records.values()].map(async record => {
      if (record.loadError) await this.load(record.runId);
      await this.save(record);
    }));
  }
  destroy(): void { this.listeners.clear(); }

  private record(id: string): RewardRecord {
    let record = this.records.get(id);
    if (!record) {
      record = { runId: id, targetPhase: 1, ledger: null, clearAmount: 0, saving: false, error: '',
        clearSaving: false, clearError: false, loading: false, loadError: '' };
      this.records.set(id, record);
    }
    return record;
  }
  private async save(record: RewardRecord): Promise<void> {
    if (record.saving || record.targetPhase <= (record.ledger?.highestPhase ?? 1)) return;
    record.saving = true; record.error = ''; this.changed(record.runId);
    try {
      const progress = await this.getProgress();
      while (record.targetPhase > (record.ledger?.highestPhase ?? 1)) {
        const target = record.targetPhase;
        try {
          const saved = await progress.awardPhase(record.runId, target);
          const oldPhase = record.ledger?.highestPhase ?? 1;
          if (saved.ledger && saved.ledger.highestPhase >= oldPhase) record.ledger = saved.ledger;
          if (saved.awardedAmount > 0 && saved.ledger) {
            // The persisted ledger supplies the result, including any delayed earlier milestone.
            let minimum = 0, remaining = saved.awardedAmount;
            for (let phase = saved.ledger.highestPhase; phase >= 2 && remaining > 0; phase--) {
              remaining -= saved.ledger.phaseAmounts[phase]!;
              minimum += phaseRewardRange(phase).min;
            }
            this.changed(record.runId, { amount: saved.awardedAmount, minimum, phase: saved.ledger.highestPhase });
          }
        } catch (error) {
          // A later phase arriving during a failed write carries the earlier entitlement forward.
          if (record.targetPhase > target) continue;
          record.error = message(error); break;
        }
      }
    } catch (error) { record.error = message(error); }
    finally { record.saving = false; this.changed(record.runId); }
  }
  private changed(id: string, award?: RewardAward): void { this.listeners.forEach(listener => listener(id, award)); }
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : '観測石を保存できませんでした。再試行してください。';
}
